import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useT } from '@/lib/i18n/useT';
import { eventsApi } from '@/features/events/api';
import type { XkEvent } from '@/lib/api/types';
import { Link } from 'react-router-dom';

/**
 * PRD §20 — Calendar v1. List + month-grid views with project/group/status
 * filters and drill-in.
 *
 * Stack:
 *   • Data source: GET /xk-event (admin context — agency or brand scope).
 *     Reporter View calls a scoped variant in Phase 3.
 *   • Views: 'list' (default — mobile-friendly chronological) and
 *     'month' (kiosk-friendly grid).
 *   • Filters: free-text, status, project. Capacity colouring waits on
 *     the §20 capacity formula in the project page; for now we colour
 *     the dot by `eventStatus` only.
 */
type CalendarView = 'list' | 'month';

const STATUS_COLORS: Record<string, string> = {
  Active: 'var(--color-success)',
  Scheduled: 'var(--color-warn)',
  Archived: 'var(--color-xk-text-muted)',
};

export function CalendarV1() {
  const t = useT();
  const [view, setView] = useState<CalendarView>('list');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['calendar', 'xk-events'],
    queryFn: async () => {
      const env = await eventsApi.listEvents();
      return env.items;
    },
  });

  const filtered = useMemo(() => {
    return (data ?? []).filter((e) => {
      if (statusFilter && e.eventStatus !== statusFilter) return false;
      if (
        filter &&
        !`${e.name ?? ''} ${e.address ?? ''}`
          .toLowerCase()
          .includes(filter.toLowerCase())
      )
        return false;
      return true;
    });
  }, [data, filter, statusFilter]);

  return (
    <section className="rounded-2xl border border-[var(--color-xk-border)] p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest opacity-60">
            {t('calendar.eyebrow')}
          </p>
          <h2 className="mt-1 text-base font-bold">{t('calendar.title')}</h2>
        </div>
        <div className="flex items-center gap-2">
          <ViewToggle view={view} onChange={setView} />
        </div>
      </header>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('calendar.searchPlaceholder')}
          className="w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-sm"
        >
          <option value="">{t('calendar.statusAll')}</option>
          <option value="Active">{t('calendar.statusActive')}</option>
          <option value="Scheduled">{t('calendar.statusScheduled')}</option>
          <option value="Archived">{t('calendar.statusArchived')}</option>
        </select>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <p className="text-sm opacity-60">{t('calendar.loading')}</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm opacity-60">{t('calendar.empty')}</p>
        ) : view === 'list' ? (
          <ListView events={filtered} />
        ) : (
          <MonthView events={filtered} />
        )}
      </div>
    </section>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: CalendarView;
  onChange: (v: CalendarView) => void;
}) {
  const t = useT();
  return (
    <div className="inline-flex overflow-hidden rounded-full border border-[var(--color-xk-border)] text-[11px] font-bold">
      {(['list', 'month'] as const).map((v) => {
        const active = v === view;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`px-3 py-1 transition-colors ${
              active
                ? 'bg-[var(--color-xk-accent)] text-[var(--color-xk-bg-deep)]'
                : 'text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]'
            }`}
          >
            {t(`calendar.view.${v}`)}
          </button>
        );
      })}
    </div>
  );
}

function ListView({ events }: { events: XkEvent[] }) {
  const sorted = [...events].sort((a, b) => {
    const ad = a.startDate ? new Date(a.startDate).getTime() : 0;
    const bd = b.startDate ? new Date(b.startDate).getTime() : 0;
    return ad - bd;
  });
  return (
    <ul className="divide-y divide-[var(--color-xk-border)]">
      {sorted.map((e) => {
        const dot = STATUS_COLORS[e.eventStatus ?? ''] ?? 'var(--color-xk-text-muted)';
        return (
          <li key={e.id} className="flex items-center gap-3 py-3">
            <span
              aria-hidden
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: dot }}
            />
            <div className="min-w-0 flex-1">
              <Link
                to={`/projects/${(e as any).projectId ?? 0}/events/${e.id}`}
                className="block truncate text-sm font-bold hover:underline"
              >
                {e.name ?? `Event ${e.id}`}
              </Link>
              <p className="truncate text-[11px] opacity-60">
                {formatRange(e.startDate, e.endDate)}
                {e.address ? ` · ${e.address}` : ''}
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-widest opacity-60">
              {e.eventStatus}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function MonthView({ events }: { events: XkEvent[] }) {
  // Centre on the earliest event's month so the grid lands where the data
  // actually lives — empty months don't dominate the view.
  const anchor = useMemo(() => {
    const sorted = [...events].sort((a, b) => {
      const ad = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bd = b.startDate ? new Date(b.startDate).getTime() : 0;
      return ad - bd;
    });
    const first = sorted[0]?.startDate ? new Date(sorted[0].startDate) : new Date();
    return new Date(first.getFullYear(), first.getMonth(), 1);
  }, [events]);
  const days: { date: Date; events: XkEvent[] }[] = useMemo(() => {
    const out: { date: Date; events: XkEvent[] }[] = [];
    const lastDay = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    for (let d = 1; d <= lastDay; d++) {
      const day = new Date(anchor.getFullYear(), anchor.getMonth(), d);
      out.push({
        date: day,
        events: events.filter((e) => {
          if (!e.startDate) return false;
          const start = new Date(e.startDate);
          return (
            start.getFullYear() === day.getFullYear() &&
            start.getMonth() === day.getMonth() &&
            start.getDate() === day.getDate()
          );
        }),
      });
    }
    return out;
  }, [anchor, events]);
  // Pad the grid with leading blanks so the first weekday lines up.
  const leadingBlanks = anchor.getDay();
  return (
    <div>
      <p className="mb-2 text-xs font-bold opacity-70">
        {anchor.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
      </p>
      <div className="grid grid-cols-7 gap-1 text-[10px]">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="py-1 text-center opacity-60">
            {d}
          </div>
        ))}
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {days.map((d) => {
          const hasEvent = d.events.length > 0;
          return (
            <div
              key={d.date.toISOString()}
              className={`rounded-md border p-1 text-[10px] ${
                hasEvent
                  ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-06)]'
                  : 'border-[var(--color-xk-border)]'
              }`}
              title={d.events.map((e) => e.name).join(', ')}
            >
              <div className="font-bold opacity-80">{d.date.getDate()}</div>
              {hasEvent && (
                <div className="mt-0.5 truncate text-[9px]">
                  {d.events[0].name}
                  {d.events.length > 1 && ` +${d.events.length - 1}`}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatRange(start?: string | null, end?: string | null): string {
  if (!start) return '';
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return e
    ? `${s.toLocaleString(undefined, opts)} – ${e.toLocaleString(undefined, opts)}`
    : s.toLocaleString(undefined, opts);
}
